import React, { useState } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getLogisticsCenters, getReorderAlerts, getTransferSuggestions } from '../services/api';

export default function Logistics() {
  const { data: centersData, loading } = useAPI(getLogisticsCenters);
  const { data: alertsData } = useAPI(getReorderAlerts);
  const { data: transfersData } = useAPI(getTransferSuggestions);
  const [selectedCenter, setSelectedCenter] = useState(null);

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading logistics data...</div>;

  const centers = centersData?.centers || [];
  const alerts = alertsData?.alerts || [];
  const transfers = transfersData?.transfers || [];

  const totalValue = centers.reduce((sum, c) => sum + (c.total_value || 0), 0);
  const totalLow = centers.reduce((sum, c) => sum + (c.low_stock_items || 0), 0);
  const totalCritical = centers.reduce((sum, c) => sum + (c.critical_items || 0), 0);

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Logistics Centers</h1>
      <p style={{ fontSize: 14, color: '#636e72', marginTop: 4 }}>
        Inventory management across {centers.length} centers
      </p>

      {/* KPI cards */}
      <div style={styles.cardGrid}>
        <StatCard label="Centers" value={centers.length} color="#6c5ce7" />
        <StatCard label="Total Inventory" value={`$${(totalValue / 1000).toFixed(0)}K`} color="#0984e3" />
        <StatCard label="Low Stock Items" value={totalLow} color="#e17055" />
        <StatCard label="Critical Alerts" value={totalCritical} color="#d63031" />
      </div>

      {/* Centers overview */}
      <div style={styles.twoCol}>
        <div>
          <h2 style={styles.sectionTitle}>Center Overview</h2>
          {centers.map(center => (
            <div
              key={center.center_id}
              style={{
                ...styles.centerCard,
                borderLeftColor: selectedCenter === center.center_id ? '#6c5ce7' : 'transparent',
              }}
              onClick={() => setSelectedCenter(
                selectedCenter === center.center_id ? null : center.center_id
              )}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{center.center_name}</div>
                  <div style={{ fontSize: 12, color: '#636e72', marginTop: 2 }}>
                    Region: {center.region} | {center.total_items} items
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>${(center.total_value / 1000).toFixed(1)}K</div>
                  <div style={{ fontSize: 11, color: '#636e72' }}>{center.utilization_pct}% capacity</div>
                </div>
              </div>

              {/* Utilization bar */}
              <div style={{ marginTop: 8 }}>
                <div style={styles.barBg}>
                  <div style={{
                    ...styles.barFill,
                    width: `${center.utilization_pct}%`,
                    background: center.utilization_pct > 80 ? '#d63031' : center.utilization_pct > 60 ? '#e17055' : '#00b894',
                  }} />
                </div>
              </div>

              {/* Status badges */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {center.low_stock_items > 0 && (
                  <span style={{ ...styles.badge, background: '#fff3cd', color: '#d68910' }}>
                    {center.low_stock_items} low stock
                  </span>
                )}
                {center.critical_items > 0 && (
                  <span style={{ ...styles.badge, background: '#ffe0e0', color: '#c0392b' }}>
                    {center.critical_items} critical
                  </span>
                )}
                {center.low_stock_items === 0 && center.critical_items === 0 && (
                  <span style={{ ...styles.badge, background: '#d4edda', color: '#155724' }}>
                    All stocked
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div>
          {/* Reorder alerts */}
          <h2 style={styles.sectionTitle}>
            Reorder Alerts
            {alerts.length > 0 && (
              <span style={{ fontSize: 12, fontWeight: 400, color: '#d63031', marginLeft: 8 }}>
                {alerts.filter(a => a.urgency === 'critical').length} critical
              </span>
            )}
          </h2>
          {alerts.length > 0 ? (
            <DataTable
              columns={[
                {
                  key: 'urgency',
                  label: '',
                  render: v => (
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                      background: v === 'critical' ? '#d63031' : v === 'warning' ? '#e17055' : '#636e72',
                    }} />
                  ),
                },
                { key: 'part_name', label: 'Part' },
                { key: 'center_name', label: 'Center' },
                { key: 'current_stock', label: 'Stock', align: 'right' },
                {
                  key: 'days_until_stockout',
                  label: 'Days Left',
                  align: 'right',
                  render: v => v ? (
                    <span style={{ color: v < 3 ? '#d63031' : v < 7 ? '#e17055' : '#636e72', fontWeight: 600 }}>
                      {v}d
                    </span>
                  ) : '-',
                },
                {
                  key: 'estimated_cost',
                  label: 'Reorder $',
                  align: 'right',
                  render: v => `$${v?.toLocaleString()}`,
                },
              ]}
              data={alerts.slice(0, 15)}
            />
          ) : (
            <div style={styles.emptyBox}>No reorder alerts - inventory levels healthy</div>
          )}

          {/* Transfer suggestions */}
          <h2 style={{ ...styles.sectionTitle, marginTop: 24 }}>Transfer Suggestions</h2>
          {transfers.length > 0 ? (
            <DataTable
              columns={[
                { key: 'part_id', label: 'Part' },
                { key: 'from_center', label: 'From' },
                { key: 'to_center', label: 'To' },
                { key: 'quantity', label: 'Qty', align: 'right' },
                {
                  key: 'priority',
                  label: 'Priority',
                  render: v => (
                    <span style={{
                      ...styles.badge,
                      background: v === 'urgent' ? '#ffe0e0' : '#e9ecef',
                      color: v === 'urgent' ? '#c0392b' : '#636e72',
                    }}>
                      {v}
                    </span>
                  ),
                },
              ]}
              data={transfers.slice(0, 10)}
            />
          ) : (
            <div style={styles.emptyBox}>No transfers needed - inventory balanced</div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    margin: '20px 0',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  centerCard: {
    padding: '16px',
    background: '#fff',
    borderRadius: 10,
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    borderLeft: '4px solid transparent',
    transition: 'all 0.15s',
  },
  barBg: {
    height: 6,
    background: '#f0f0f0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
  },
  emptyBox: {
    padding: 20,
    background: '#fff',
    borderRadius: 10,
    fontSize: 13,
    color: '#00b894',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
};
